/**
 * Class base statistics + automatic level growth (M7-D2 Stage B).
 *
 * Architecture supports additional classes without rewriting combat math.
 * Magnitudes: APPROVED G1 (A3 growth) for the full Class Level 1–60 curve.
 * Retune only via this module.
 *
 * Does not invent Strength / Dex / Int / Speed.
 * Crit / Block / Dodge / Counter remain talent/gear-driven (optional
 * class starting values may be added later without auto growth).
 */

import type { ClassId } from '../persistence/schema';
import { CLASS_LEVEL_CAP } from '../persistence/schema';

export interface ClassBaseStatProfile {
  classId: ClassId;
  /** Flat max HP at class level 1 (before talent % and gear flats). */
  baseMaxHp: number;
  /** Added per class level after 1. */
  hpPerLevel: number;
  /** Inherent flat Attack at level 1 (not weapon swing; not OH-penalised). */
  baseFlatAttack: number;
  /** Flat Attack gained per class level after 1. */
  attackPerLevel: number;
  /** Inherent flat Armour at level 1. */
  baseFlatArmour: number;
  /**
   * Flat Armour gained per class level after 1 (may be fractional;
   * floored when deriving totals). Grows slower than Attack by design.
   */
  armourPerLevel: number;
}

/**
 * Approved Vanguard profile (G1 / A3).
 * L1 HP 40 · Attack 2 · Armour 3
 * Growth: +2 HP / +0.5 Attack / +0.25 Armour per class level after 1.
 * Class level cap remains 60.
 */
export const VANGUARD_BASE_STATS: ClassBaseStatProfile = {
  classId: 'vanguard',
  baseMaxHp: 40,
  hpPerLevel: 2,
  baseFlatAttack: 2,
  attackPerLevel: 0.5,
  baseFlatArmour: 3,
  armourPerLevel: 0.25,
};

const PROFILES: Record<ClassId, ClassBaseStatProfile> = {
  vanguard: VANGUARD_BASE_STATS,
};

export function getClassBaseStatProfile(
  classId: ClassId = 'vanguard',
): ClassBaseStatProfile {
  return PROFILES[classId] ?? VANGUARD_BASE_STATS;
}

export function clampClassLevel(level: number): number {
  const n = Math.floor(level);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(CLASS_LEVEL_CAP, n);
}

/** Levels above 1 that grant automatic growth. */
export function classGrowthSteps(classLevel: number): number {
  return Math.max(0, clampClassLevel(classLevel) - 1);
}

export interface DerivedClassBaseStats {
  classId: ClassId;
  classLevel: number;
  /** Pre-talent, pre-gear max HP from class curve. */
  classMaxHp: number;
  /** Flat Attack (base + level). Integer at this boundary. */
  flatAttack: number;
  /** Flat Armour (base + floored level growth). */
  flatArmour: number;
  /** Breakdown for UI / sims. */
  breakdown: {
    baseMaxHp: number;
    levelMaxHp: number;
    baseFlatAttack: number;
    levelFlatAttack: number;
    baseFlatArmour: number;
    levelFlatArmour: number;
  };
}

/**
 * Derive class-owned base flats from class id + level.
 * Prefer this over storing redundant calculated combat values on the save.
 */
export function deriveClassBaseStats(
  classId: ClassId = 'vanguard',
  classLevel: number = 1,
): DerivedClassBaseStats {
  const profile = getClassBaseStatProfile(classId);
  const level = clampClassLevel(classLevel);
  const steps = classGrowthSteps(level);
  const levelMaxHp = steps * profile.hpPerLevel;
  const levelFlatAttack = steps * profile.attackPerLevel;
  const levelFlatArmour = Math.floor(steps * profile.armourPerLevel);
  return {
    classId: profile.classId,
    classLevel: level,
    classMaxHp: Math.max(1, profile.baseMaxHp + levelMaxHp),
    flatAttack: Math.max(0, Math.floor(profile.baseFlatAttack + levelFlatAttack)),
    flatArmour: Math.max(0, profile.baseFlatArmour + levelFlatArmour),
    breakdown: {
      baseMaxHp: profile.baseMaxHp,
      levelMaxHp,
      baseFlatAttack: profile.baseFlatAttack,
      levelFlatAttack,
      baseFlatArmour: profile.baseFlatArmour,
      levelFlatArmour,
    },
  };
}
